import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DialogContent, Grid, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { StatusCell } from "components/atoms";
import {
  AppTable,
  ImportDocumentDialog,
  ImportErrorDialog,
} from "components/molecules";
import { ColorHelper, StatusHelper, TableHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import { isArray, isEmpty, isUndefined } from "lodash";
import {
  EImportType,
  ELoadingOrderStatus,
  IDownloadExcelRequest,
  IGetPeriodCycleCountRequest,
  IImportLoadingOrderDocument,
  ILoadingOrder,
  IPaginationRequest,
} from "model";
import { ECountType } from "model/Type/ECountType";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  CycleCountActions,
  DownloadActions,
  LoadingOrderActions,
} from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { CycleCountApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import ImportPeriodicCycleCountType from "./components/ImportPeriodicCycleCountType";

const PeriodicCycleCount = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const action = useAppSelector((state) => state.CycleCountReducer.action);
  const errors = useAppSelector((state) => state.ErrorReducer.errors);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const [refresh, setRefresh] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    searchText: searchText,
  });
  const [file, setFile] = useState<FileList | null>(null);
  const [currentItem, setCurrentItem] = useState<ILoadingOrder | undefined>(
    undefined
  );

  const [open, setOpen] = useState<boolean>(false);

  const [importFailedDialogVisible, setImportFailedDialogVisible] =
    useState<boolean>(false);

  const [importType, setImportType] = useState<EImportType>(
    EImportType.IMPORT_NEW
  );
  const isImportNewDocs = importType === EImportType.IMPORT_NEW;

  const onCellClick = (event: any) => {
    if (event.colDef.field !== "action") {
      history.push(`/receive/periodic-cycle-count/${event.row.id}`);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setOpen(false);
    setCurrentItem(undefined);
  };
  const handleConfirmImport = () => {
    _importLoadingOrderDocument();
  };

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };
  const { loading, result } = usePaginationRequest<
    IGetPeriodCycleCountRequest,
    any[]
  >(
    request,
    CycleCountApiService.getPeriodicCycleCount,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _importLoadingOrderDocument = () => {
    const request: IImportLoadingOrderDocument = {
      file: file?.item(0) as File,
      LOImportCode: currentItem?.doImportCode || "",
      loadingOrderHeaderId: currentItem?.id || "",
      loadingOrderHeaderDescription: currentItem?.description || "",
    };
    // dispatch(LoadingOrderActions.importLoadingOrderDocument.request(request));
  };

  const importDocsSuccess =
    action.includes(
      CycleCountActions.importPeriodicCycleCountByProduct.successName
    ) ||
    action.includes(
      CycleCountActions.importPeriodicCycleCountByLocation.successName
    );
  const importDocsFailed =
    action.includes(
      CycleCountActions.importPeriodicCycleCountByProduct.failedName
    ) ||
    action.includes(
      CycleCountActions.importPeriodicCycleCountByLocation.failedName
    );

  useEffect(() => {
    if (importDocsSuccess) {
      handleCancel();
      setRefresh(true);
    }
    if (importDocsFailed) {
      if (isArray(errors) || !isUndefined(errors)) {
        setOpen(false);
        setRefresh(true);
      }
    }
  }, [action]);

  const onDownloadPeriodicCycleCountProductForm = async () => {
    const params: IDownloadExcelRequest = {
      fileName: EXPORT_FILE_NAME.cycleCountProductForm,
    };
    dispatch(
      DownloadActions.downloadPeriodicCycleCountProductForm.request(params)
    );
  };

  const onDownloadPeriodicCycleCountLocationForm = async () => {
    const params: IDownloadExcelRequest = {
      fileName: EXPORT_FILE_NAME.cycleCountProductForm,
    };
    dispatch(
      DownloadActions.downloadPeriodicCycleCountLocationForm.request(params)
    );
  };

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "documentNbr",
      headerName: I18nTranslator(translations.periodicCycleCount.documentCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "fromDate",
      headerName: I18nTranslator(translations.periodicCycleCount.fromDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "toDate",
      headerName: I18nTranslator(translations.periodicCycleCount.toDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "status",
      headerName: I18nTranslator(translations.status),
      align: "left",
      size: "small",
      renderCell: ({ value }) => (
        <StatusCell
          bgcolor={ColorHelper.getLoadingOrderStatusColor(
            value as ELoadingOrderStatus
          )}
        >
          {StatusHelper.getLOStatusString(value)}
        </StatusCell>
      ),
    }),
    TableHelper.createCell({
      field: "countType",
      headerName: I18nTranslator(translations.periodicCycleCount.countType),
      size: "small",
      align: "left",
      renderCell: ({ row: { countType } }) => (
        <CycleCountTypeCell value={countType} />
      ),
    }),
    TableHelper.createCell({
      field: "totalLocationNeedToCheck",
      headerName: I18nTranslator(
        translations.periodicCycleCount.totalLocationNeedCheck
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "totalLocationVerified",
      headerName: I18nTranslator(
        translations.periodicCycleCount.totalLocationChecked
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "totalLocationUpdated",
      headerName: I18nTranslator(
        translations.periodicCycleCount.totalLocationUpdated
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "totalLocationCorrect",
      headerName: I18nTranslator(
        translations.periodicCycleCount.totalLocationCorrect
      ),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "totalLocationWrong",
      headerName: I18nTranslator(
        translations.periodicCycleCount.totalLocationWrong
      ),
      size: "small",
      align: "right",
    }),
  ];

  return (
    <Fragment>
      <AppTable<any>
        title={I18nTranslator(translations.routes.periodicCycleCount)}
        loading={loading}
        data={result}
        columns={COLUMNS}
        onCellClick={onCellClick}
        onSearch={handleSearch}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchPeriodicCycleCountDocument
        )}
        onChangePage={handleChangePage}
        actionColumnProps={{
          hideDelete: true,
          hideEdit: true,
        }}
        hideDownload
        onAddNewClick={() => setOpen(true)}
        onRowPressImport={(row) => {
          setImportType(EImportType.UPDATE);
          setCurrentItem(row);
        }}
        onPageSizeChange={_handlePageSizeChange}
        hideImport
        headerButtonsLeft={
          <Stack direction={"row"} spacing={2} sx={{ marginLeft: "10px" }}>
            <LoadingButton
              startIcon={<Download />}
              loading={downloading}
              variant="contained"
              onClick={onDownloadPeriodicCycleCountProductForm}
            >
              {I18nTranslator(translations.button.exportProductCycleCountDocs)}
            </LoadingButton>
            <LoadingButton
              startIcon={<Download />}
              loading={downloading}
              variant="contained"
              onClick={onDownloadPeriodicCycleCountLocationForm}
            >
              {I18nTranslator(translations.button.exportLocationCycleCountDocs)}
            </LoadingButton>
          </Stack>
        }
      />
      <ImportDocumentDialog
        title={I18nTranslator(
          isImportNewDocs
            ? translations.receive.importDocs
            : translations.receive.updateDocs
        )}
        open={open}
        handleConfirm={handleConfirmImport}
        handleClose={handleCancel}
        loading={action.includes(
          LoadingOrderActions.importLoadingOrderDocument.requestName
        )}
        fileName={!isEmpty(file) && file ? file[0].name : ""}
        isImportNewDocs={isImportNewDocs}
        updateItemName={currentItem?.doImportCode || ""}
      />
      <ImportErrorDialog
        title={I18nTranslator(translations.receive.importError)}
        open={importFailedDialogVisible}
        handleReImport={() => {
          setImportFailedDialogVisible(false);
          handleCancel();
        }}
      >
        <DialogContent sx={{ maxHeight: "50vh" }}>
          {!isUndefined(errors) &&
            isArray(errors) &&
            (errors as string[]).map((error) => {
              return <Typography>{error}</Typography>;
            })}
        </DialogContent>
      </ImportErrorDialog>
      <ImportPeriodicCycleCountType
        open={open}
        onClose={() => setOpen(false)}
      />
    </Fragment>
  );
};

const CycleCountTypeCell = ({ value }: { value: string }) => {
  return (
    <StatusCell bgcolor={value === ECountType.PRODUCT ? "#0252b5" : "#018550"}>
      {StatusHelper.getCountTypeString(value as ECountType)}
    </StatusCell>
  );
};

export default PeriodicCycleCount;
